import React, { useEffect, useState } from "react";
import {
  VariantControl,
  VariantDot,
  type VariantType,
} from "../../components/variant/VariantControl";
import type { NewPropertyState } from "../../../usePropertyStateV2";
import { type ComboBoxOptionType, DiscreteInput } from "../../components/input";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { JUSTIFY_CONTENT_OPTIONS } from "../../components/variant/variantOptions";
import { PropertyType } from "@/tailwind/tailwindUtils";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface WrapContentProps {
  flexDirectionState: NewPropertyState;
  justifyContentState: NewPropertyState;
  alignItemsState: NewPropertyState;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const WrapContentField: React.FC<WrapContentProps> = React.memo(
  ({
    flexDirectionState,
    justifyContentState,
    alignItemsState,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
  }) => {
    return (
      <div className="flex flex-col items-start justify-between gap-1 relative">
        <div className="text-xs  text-text-secondary font-normal">
          Wrap content
        </div>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="w-2/3 relative">
              <VariantDot valueByVariant={justifyContentState.valueByVariant} />
              <DiscreteInput
                options={JUSTIFY_CONTENT_OPTIONS}
                selected={justifyContentState.propertyValue}
                set={justifyContentState.isSet}
                onOptionSelected={(option) => {
                  pickTailwindClassForProperty({
                    option: option.option,
                    propertyType: PropertyType.JUSTIFY_CONTENT,
                    setTemporaryDraggingValue:
                      justifyContentState.setTemporaryDraggingValue,
                  });
                }}
                onDeselect={() => {
                  clearTailwindClassForProperty({
                    propertyType: PropertyType.JUSTIFY_CONTENT,
                    setTemporaryDraggingValue:
                      justifyContentState.setTemporaryDraggingValue,
                  });
                }}
              />
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent className="p-0 border-none">
            <VariantControl
              valueByVariant={justifyContentState.valueByVariant}
              onVariantChange={(variant: VariantType, value) => {
                const option = JUSTIFY_CONTENT_OPTIONS.find(
                  (opt) => opt.key === value,
                );
                if (option) {
                  pickTailwindClassForProperty({
                    option: option.option,
                    propertyType: PropertyType.JUSTIFY_CONTENT,
                    setTemporaryDraggingValue:
                      justifyContentState.setTemporaryDraggingValue,
                    variant,
                  });
                }
              }}
              onClearVariant={(variant: VariantType) => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.JUSTIFY_CONTENT,
                  setTemporaryDraggingValue:
                    justifyContentState.setTemporaryDraggingValue,
                  variant,
                });
              }}
              currentFlexDirection={flexDirectionState.propertyValue}
              currentJustifyContent={justifyContentState.propertyValue}
              currentAlignItems={alignItemsState.propertyValue}
              controlType="justify-content"
            >
              {(variant, expanded, setExpanded) => {
                if (!expanded || !variant) return null;
                const variantValue =
                  justifyContentState.valueByVariant[variant]?.value || "";
                return (
                  <DiscreteInput
                    options={JUSTIFY_CONTENT_OPTIONS}
                    onOptionSelected={(discreteOption) => {
                      pickTailwindClassForProperty({
                        option: discreteOption.option,
                        propertyType: PropertyType.JUSTIFY_CONTENT,
                        setTemporaryDraggingValue:
                          justifyContentState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    selected={variantValue}
                    set={!!variantValue}
                    onDeselect={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.JUSTIFY_CONTENT,
                        setTemporaryDraggingValue:
                          justifyContentState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />
                );
              }}
            </VariantControl>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    );
  },
);

export default WrapContentField;
